import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

/**
 * Opens the heavyweight file-preview dialogs, downloading their code on first use.
 *
 * The spreadsheet, document-editor and PDF-viewer previews are backed by Syncfusion packages that
 * together weigh ~24 MB. Importing their components directly put all of that in the eager bundle —
 * every user paid for it on the login screen, whether or not they ever previewed a file. Going
 * through `import()` here keeps them in their own chunks, fetched the first time a preview opens.
 *
 * Each method returns a promise for the dialog ref, so callers that need `afterClosed()` can await
 * it. The components are standalone, so no NgModule has to be instantiated alongside them.
 */
@Injectable({ providedIn: 'root' })
export class LazyPreviewService {
  constructor(private dialog: MatDialog) {}

  /** Spreadsheet preview — .csv / .xls / .xlsx */
  async openCsvPreview(config: MatDialogConfig): Promise<MatDialogRef<any>> {
    const { CsvPreviewComponent } = await import(
      '../modules/shared/csv-preview/csv-preview.component'
    );
    return this.dialog.open(CsvPreviewComponent, config);
  }

  /** Document-editor preview — .doc / .docx and plain text */
  async openTextPreview(config: MatDialogConfig): Promise<MatDialogRef<any>> {
    const { TextPreviewComponent } = await import(
      '../modules/shared/text-preview/text-preview.component'
    );
    return this.dialog.open(TextPreviewComponent, config);
  }

  /** PDF preview with annotation/editing tools */
  async openPdfPreview(config: MatDialogConfig): Promise<MatDialogRef<any>> {
    const { DocumentPdfPreviewDialogComponent } = await import(
      '../modules/document/document-pdf-preview-dialog/document-pdf-preview-dialog.component'
    );
    return this.dialog.open(DocumentPdfPreviewDialogComponent, config);
  }
}
